import { useCampaignStore } from '@/stores/campaignStore';
import { useLeafletingStore } from '@/stores/leafletingStore';
import { useNavigationStore } from '@/stores/navigationStore';

/**
 * Refreshes data based on the access scope from token redemption
 */
export const refreshDataForAccessScope = async (
  accessScope: any,
  branchId: string
) => {
  try {
    const navigationStore = useNavigationStore.getState();
    const campaignStore = useCampaignStore.getState();
    const leafletingStore = useLeafletingStore.getState();
    
    // Always refresh branches
    const branches = await navigationStore.fetchBranches();

    // Update the current branch
    const branch = branches?.find(b => b.branch_short_name === branchId);
    if (branch) {
      await navigationStore.changeBranch(branch);
      await campaignStore.syncWithNavigationStore();
      await leafletingStore.syncWithNavigationStore();
    }

    // Refresh campaigns for the branch
    await navigationStore.fetchCampaigns(branchId);

    // Get all campaigns to refresh their data
    const campaigns = navigationStore.getCampaigns(branchId) || [];

    // Refresh data based on access scope
    if (accessScope?.battle_plans && accessScope.battle_plans.length > 0) {
      // Refresh battle plans and specific engagements for all campaigns
      for (const campaign of campaigns) {
        await refreshCampaignBattlePlansAndEngagements(
          campaign.boundary_id,
          accessScope.battle_plans // Pass the battle plans to fetch specific engagements
        );
      }
    }

    if (accessScope?.rounds && accessScope.rounds.length > 0) {
      // Refresh rounds and specific route assignments for all campaigns
      for (const campaign of campaigns) {
        await refreshCampaignLeafletingData(
          campaign.boundary_id,
          accessScope.rounds // Pass the rounds to fetch specific route assignments
        );
      }
    }

    return true;
  } catch (error) {
    console.error('[TokenRedemption] Error refreshing data for access scope:', error);
    return false;
  }
};

/**
 * Refreshes battle plans and engagements for a campaign.
 * Fetches all battle plans, then fetches specific engagements for battle plans in the access scope.
 */
const refreshCampaignBattlePlansAndEngagements = async (
    campaignId: string,
    battlePlansInScope?: any[]
    ): Promise<void> => {
    const campaignStore = useCampaignStore.getState();
    const navigationStore = useNavigationStore.getState();
    
    // First, set the campaign context
    const branchId = navigationStore.getSelectedBranch()?.branch_short_name;
    if (branchId) {
      const campaigns = navigationStore.getCampaigns(branchId);
      const campaign = campaigns?.find(c => c.boundary_id === campaignId);
      if (campaign) {
        navigationStore.setSelectedCampaignId(branchId, campaign.boundary_id);
        campaignStore.syncWithNavigationStore();
      }
    }
    
    // Refresh all battle plans for the campaign
    await campaignStore.fetchBattlePlans();

    // If we have specific battle plans in the access scope, fetch their engagements
    if (battlePlansInScope && battlePlansInScope.length > 0) {
      for (const battlePlan of battlePlansInScope) {
        if (battlePlan.battle_plan_id) {
          try {
            // Try to fetch engagements for this specific battle plan (with electoral division ids)
            await campaignStore.fetchEngagements(battlePlan.battle_plan_id, true);
          } catch (error) {
            // This battle plan might not be in this campaign, continue to next
            continue;
          }
        }
      }
    }
};

/**
 * Refreshes leafleting rounds and route assignments for a campaign.
 * Fetches all rounds, then fetches specific route assignments for rounds in the access scope.
 */
const refreshCampaignLeafletingData = async (
    campaignId: string,
    roundsInScope?: any[]
    ): Promise<void> => {
    const leafletingStore = useLeafletingStore.getState();
    const navigationStore = useNavigationStore.getState();
    
    // First, set the campaign context
    const branchId = navigationStore.getSelectedBranch()?.branch_short_name;
    if (branchId) {
      const campaigns = navigationStore.getCampaigns(branchId);
      const campaign = campaigns?.find(c => c.boundary_id === campaignId);
      if (campaign) {
        navigationStore.setSelectedCampaignId(branchId, campaign.boundary_id);
        leafletingStore.syncWithNavigationStore();
      }
    }
    
    // Refresh all rounds for the campaign
    await leafletingStore.fetchRounds();

    // If we have specific rounds in the access scope, fetch their route assignments
    if (roundsInScope && roundsInScope.length > 0) {
      for (const round of roundsInScope) {
        if (round.round_id) {
          try {
            // Try to fetch route assignments for this specific round
            await leafletingStore.fetchRoutes(round.round_id);
          } catch (error) {
            // This round might not be in this campaign, continue to next
            continue;
          }
        }
      }
    }
};

/**
 * Redeems access token and refreshes relevant data for the given path
 */
export const redeemAccessToken = async (
  token: string
) => {
  
  try {
    const navigationStore = useNavigationStore.getState();
    const redemptionResult = await navigationStore.redeemAccessToken(token);
    
    if (!redemptionResult.success) {
        return null;
    }
    
    // Get branch ID from the token redemption response
    const branchId = redemptionResult.branchId;
    if (!branchId) {
      console.error('[TokenRedemption] No branch ID in token redemption response');
      return null;
    }
    
    // Refresh data based on the access scope from the token redemption response
    const refreshSuccess = await refreshDataForAccessScope(
      redemptionResult.accessScope,
      branchId
    );
    
    if (!refreshSuccess) {
      return null;
    }
    
    // Return the original pathname (the token redemption response tells us what data to refresh)
    return true;
    
  } catch (error) {
    console.error('[TokenRedemption] Token redemption failed:', error);
    throw error; // Re-throw the error so calling code can handle it
  }
};
